import { FilterBarModule, UtilityMessageComponent } from '@addToEvent/add-to-event-ui';
import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { EmailThread, EmailThreadType } from '@features/email/interfaces/email-thread.interface';
import { Email } from '@features/email/interfaces/email.interface';
import { EmailThreadsService } from '@features/email/services/email-threads.service';
import { Store } from '@ngrx/store';
import { FormComponentsControlDirective } from '@shared/directives/forms/form-components-wrapper.directive';
import { ModelValues } from '@shared/models/value-type-interface';
import { NotLeadDialogComponent, NotLeadAction } from '@features/email/components/not-lead-dialog/not-lead-dialog.component';
import { EmailSettingsService } from '@shared/services/email/email-settings.service';
import { GoogleAuthService } from '@shared/services/integrations/google/auth/google-auth.service';
import { GmailService } from '@shared/services/integrations/google/gmail/gmail.service';
import { SharedModule } from '@shared/shared.module';
import { appQuery, AppState, fromAppActions } from '@shared/state';
import { format, isToday } from 'date-fns';
import { enGB } from 'date-fns/locale';
import { catchError, debounceTime, distinctUntilChanged, finalize, first, firstValueFrom, map, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { GoogleAuthStatus } from '../../../../shared/services/integrations/google/auth/google-auth-status-interface';
import { Listing } from '@marketplace/api';
import { FeatureToggleDirective } from '@shared/directives/feature-toggle/feature-toggle.directive';
import { CustomerService } from '@features/customers/services/customer.service';
import { EnquiriesService } from '@features/enquiries/services/enquiries.service';
import { NotificationsService, NotificationTypes } from '@shared/services/notifications/notifications.service';
import { Enquiry } from '@features/enquiries/interfaces/enquiry.interface';
import { RoutesService } from '@shared/services/routes.service';

@Component({
  selector: 'email-inbox-component',
  templateUrl: './email-inbox.component.html',
  imports: [
    SharedModule,
    CommonModule,
    RouterModule,
    UtilityMessageComponent,
    FormComponentsControlDirective,
    FilterBarModule,
    FeatureToggleDirective,
    NotLeadDialogComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class EmailInboxComponent implements OnInit, OnDestroy {
  title = 'Emails';
  hideComposeButton = false;
  hideTitle = false;
  filterSearchFocused = false;
  isConnected = false;
  inbox: Email[] = [];
  loading = false;
  refreshing = false;
  pageSize = 50;
  private readonly leadsDisplayPageSize = 50;
  maxTotalEmails = Infinity;
  pageTokens: (string | null)[] = [null];
  pageIndex = 0;
  approxTotal: number | null = null;
  skeletonRows = Array.from({ length: 50 });
  private _allLeads: Email[] = [];
  emptyTitle = '';
  emptyMessage = '';
  /**
   * Map keyed on composite id (`threadId::messageId` for aggregator records, `threadId` for thread-level records)
   * to EmailThread record. Prefers enquiry-type records when multiple exist for the same key.
   */
  emailThreadMap = new Map<string, EmailThread>();
  /** Composite ids that have at least one DB record with type 'enquiry', 'not_lead', or 'auto_not_a_lead' */
  private _categorizedThreadIds = new Set<string>();
  importingEmailId: string | null = null;
  listing: Listing | null = null;
  notLeadDialogMessage: Email | null = null;
  filters = [];
  pagination: { page: number; pageCount: number };
  isMasquerading = false;
  automaticEmailParsing: boolean | null = null;
  connectedEmail: string | null = null;
  importingAllLeads = false;
  importAllLeadsCooldown = false;
  linkRoute = '';
  linkName = '';
  private bannerClickedAt: number = 0;
  private static readonly BANNER_COOLDOWN_MS = 5 * 60 * 1000;
  private static readonly INFO_BANNER_DISMISSED_KEY = 'emailInboxInfoBannerDismissed';
  infoBannerDismissed = localStorage.getItem(EmailInboxComponent.INFO_BANNER_DISMISSED_KEY) === 'true';
  private readonly _importCooldownKey = 'importAllLeadsCooldownUntil';
  private readonly _importCooldownMs = 5 * 60 * 1000;
  private _cooldownTimer: ReturnType<typeof setTimeout> | null = null;
  private destroy$ = new Subject<void>();

  // 🔎 search state
  private search$ = new Subject<string>();
  searchTerm = '';
  listingUuid = '';

  constructor(
    private _googleAuthService: GoogleAuthService,
    private _gmailService: GmailService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _store: Store<AppState>,
    private _emailThreadsService: EmailThreadsService,
    private _enquiriesService: EnquiriesService,
    private _customerService: CustomerService,
    private _notificationsService: NotificationsService,
    private _emailSettingsService: EmailSettingsService,
    private _routesService: RoutesService
  ) {}

  private get isSuggestedLeadsMode(): boolean {
    return this.isLeadsMode && !this.isAllEmailsMode && !this.isNotLeadsMode && !this.isAutoRejectedMode;
  }

  private setPagination() {
    const total = this.approxTotal ?? 0;
    const size = this.isSuggestedLeadsMode ? this.leadsDisplayPageSize : this.pageSize;
    const pageCount = Math.max(1, Math.ceil(total / size));
    this.pagination = { page: this.pageIndex + 1, pageCount };
  }

  private buildQuery(): string {
    const term = this.searchTerm?.trim();
    return term;
  }

  ngOnInit() {
    this._checkImportCooldown();

    this._route.data.subscribe(data => {
      if (data?.hideTitle) {
        this.hideTitle = data.hideTitle;
      }
      if (data?.title) {
        this.title = data.title;
      }
      if (data?.hideComposeButton) {
        this.hideComposeButton = data.hideComposeButton;
      }
      if (data?.link) {
        this._routesService
          .getRoutes([data.link])
          .pipe(
            first(),
            map(([url]) => url.route)
          )
          .subscribe(route => {
            this.linkRoute = route;
          });
      }
      if (data?.linkName) {
        this.linkName = data.linkName ?? 'link';
      }
      if (this.isLeadsMode) {
        this.maxTotalEmails = 200;
      }
    });
    this._store
      .select(appQuery.getIsMasquerading)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.isMasquerading = !!val;
        // if user toggles masquerading while on the page, reflect immediately
        if (this.isMasquerading) {
          // stop any spinners and show empty list
          this.loading = false;
          this.refreshing = false;
          this.inbox = [];
          this._allLeads = [];
          this.approxTotal = 0;
          this.pageTokens = [null];
          this.pageIndex = 0;
          this.setPagination();
        }
      });

    const qp = this._route.snapshot.queryParamMap;
    this.searchTerm = qp.get('q') ?? '';

    this.search$.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(async term => {
      this.searchTerm = term ?? '';
      this.pageTokens = [null];
      this.pageIndex = 0;
      this.approxTotal = null;
      this.inbox = [];
      this.pagination = null;
      this._allLeads = [];

      await this._router.navigate([], {
        relativeTo: this._route,
        queryParams: { page: 1, pageToken: null, q: this.searchTerm || null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });

      await this.fetchPage(0);
    });

    this.loading = true;
    this._googleAuthService
      .getStatus$({ scope: 'email' })
      .pipe(first(), takeUntil(this.destroy$))
      .subscribe({
        next: async (googleAuthStatus: GoogleAuthStatus) => {
          this.isConnected = !!googleAuthStatus?.connected;
          this.automaticEmailParsing = googleAuthStatus?.automaticEmailParsing ?? null;
          this.connectedEmail = googleAuthStatus?.email ?? null;

          // short-circuit when not connected OR masquerading
          if (!this.isConnected || this.isMasquerading) {
            this.loading = false;
            this.inbox = [];
            this.approxTotal = 0;
            this.pageTokens = [null];
            this.pageIndex = 0;
            this.setPagination();
            return;
          }

          const tokenFromUrl = qp.get('pageToken');
          const pageFromUrl = Math.max(1, Number(qp.get('page') || 1));
          const targetIndex = Math.max(0, pageFromUrl - 1);

          if (!this.isSuggestedLeadsMode) {
            await this.ensureTokenFor(targetIndex); // uses current q
            if (tokenFromUrl !== null && tokenFromUrl !== undefined) {
              this.pageTokens[targetIndex] = tokenFromUrl || null;
            }
          }

          this.loading = false;
          await this.fetchPage(targetIndex);
        },
        error: () => {
          this.isConnected = false;
        }
      });

    this._store
      .select(appQuery.getListing)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (value: Listing) => {
          this.listingUuid = value?.uuid;
          this.listing = value ?? null;
        }
      });

    this.buildFilters();
  }

  private buildFilters() {
    if (this.isLeadsMode) {
      const leadsFilters = [
        {
          name: 'Suggested',
          icon: 'radar',
          url: '../inbox'
        },
        {
          name: 'All Mail',
          icon: 'inbox-v3',
          url: '../all-emails'
        }
      ];

      this.filters = leadsFilters;
    } else {
      this.filters = [
        {
          name: 'Inbox',
          icon: 'inbox-v3',
          url: '../inbox'
        },
        {
          name: 'Sent',
          icon: 'send-v3',
          url: '../sent'
        },
        {
          name: 'Starred',
          icon: 'star-v3',
          url: '../starred'
        },
        {
          name: 'Trash',
          icon: 'trash',
          url: '../trash'
        },
        {
          name: 'All Mail',
          icon: 'email-v3',
          url: '../all-mail'
        }
      ];
    }
  }

  get isLeadsMode(): boolean {
    return !!this._route.snapshot.data?.leadsMode;
  }

  get isImportedLeadsMode(): boolean {
    return !!this._route.snapshot.data?.importedLeads;
  }

  get isNotLeadsMode(): boolean {
    return !!this._route.snapshot.data?.notLeads;
  }

  get isAutoRejectedMode(): boolean {
    return !!this._route.snapshot.data?.autoRejected;
  }

  get isAllEmailsMode(): boolean {
    return !!this._route.snapshot.data?.allEmailsMode;
  }

  get isAllMailMode(): boolean {
    return !!this._route.snapshot.data?.allMailMode;
  }

  private getLabelFromRoute(route: ActivatedRoute = this._route): 'INBOX' | 'SENT' | 'STARRED' | 'TRASH' {
    const path = route.routeConfig?.path?.toLowerCase() ?? 'inbox';

    switch (path) {
      case 'sent':
        this.emptyTitle = 'Your sent folder is empty';
        this.emptyMessage = 'You have no sent emails in this folder. Try refreshing to check for new emails.';
        break;
      case 'starred':
        this.emptyTitle = 'You currently have no starred emails.';
        this.emptyMessage = 'Star an email in your Gmail inbox, press refresh, and it will appear here';
        break;
      case 'trash':
        this.emptyTitle = 'Your trash folder is empty';
        this.emptyMessage = 'You have no emails in the trash folder.';
        break;
      case 'all-mail':
        this.emptyTitle = 'No mail found';
        this.emptyMessage = 'Your mailbox has no messages in this view.';
        break;
      default:
        if (this.searchTerm) {
          this.emptyTitle = 'No messages found';
          this.emptyMessage = `You have no emails matching the search term '${this.searchTerm}'. Try refreshing to check for new emails.`;
        } else {
          this.emptyTitle = 'Your inbox is empty';
          this.emptyMessage = 'You have no emails in this folder. Try refreshing to check for new emails.';
        }
    }

    switch (path) {
      case 'sent':
        return 'SENT';
      case 'starred':
        return 'STARRED';
      case 'trash':
        return 'TRASH';
      default:
        return 'INBOX';
    }
  }

  private get labelIds(): string[] {
    return [this.getLabelFromRoute()];
  }

  async fetchPage(index: number): Promise<void> {
    if (this.loading) return;

    // guard: do nothing when masquerading
    if (this.isMasquerading) {
      this.inbox = [];
      this.approxTotal = 0;
      this.pageIndex = 0;
      this.pageTokens = [null];
      this.setPagination();
      return;
    }

    this.loading = true;
    try {
      if (this.isAllEmailsMode) {
        await this.fetchAllEmails(index);
        return;
      }

      if (this.isNotLeadsMode) {
        await this.fetchLeadsOfType(index, EmailThreadType.NotLead);
        return;
      }

      if (this.isAutoRejectedMode) {
        await this.fetchLeadsOfType(index, EmailThreadType.AutoNotALead);
        return;
      }

      if (this.isLeadsMode) {
        await this.fetchLeads(index);
        return;
      }

      if (this.isAllMailMode) {
        const pageToken = this.pageTokens[index] || undefined;
        const resp = await firstValueFrom(
          this._gmailService.listMessages$({
            maxResults: this.pageSize,
            pageToken,
            q: [this.buildQuery(), '-in:sent -in:drafts -in:chats'].filter(Boolean).join(' ')
          })
        );
        // Masquerading can switch on while a fetch is in flight — never show its result.
        if (this.isMasquerading) return;
        if (typeof resp?.resultSizeEstimate === 'number' && this.approxTotal === null) {
          this.approxTotal = Math.min(this.maxTotalEmails, resp.resultSizeEstimate);
        }
        this.inbox = resp?.data ?? [];
        this.pageIndex = index;
        this.pageTokens[index + 1] = resp?.nextPageToken ?? null;
        this.setPagination();
        await this.loadEmailThreads();
        return;
      }

      const pageToken = this.pageTokens[index] || undefined;
      const resp = await firstValueFrom(
        this._gmailService.listMessages$({
          maxResults: this.pageSize,
          pageToken,
          labelIds: this.labelIds,
          q: this.buildQuery()
        })
      );
      if (this.isMasquerading) return;

      if (typeof resp?.resultSizeEstimate === 'number' && this.approxTotal === null) {
        this.approxTotal = Math.min(this.maxTotalEmails, resp.resultSizeEstimate);
      }

      this.inbox = resp?.data ?? [];

      this.pageIndex = index;
      this.pageTokens[index + 1] = resp?.nextPageToken ?? null;
      this.setPagination();

      await this.loadEmailThreads();
    } catch (err) {
      console.error('Failed to load Gmail messages', err);
    } finally {
      this.loading = false;
    }
  }

  private async fetchLeads(index: number): Promise<void> {
    if (!this._allLeads.length) {
      await this._fetchAllLeads();
    }
    const start = index * this.leadsDisplayPageSize;
    this.inbox = this._allLeads.slice(start, start + this.leadsDisplayPageSize);
    this.pageIndex = index;
    this.approxTotal = this._allLeads.length;
    this.setPagination();
  }

  private async _fetchAllLeads(): Promise<void> {
    let pageToken: string | undefined = undefined;
    const allEmails: Email[] = [];

    while (allEmails.length < this.maxTotalEmails) {
      const dateFilter = 'newer_than:30d';
      // Mirror the auto-parser's label exclusion (EXCLUDED_LABEL_IDS = SENT,
      // DRAFT, CHAT, SPAM, TRASH) instead of locking to the INBOX label, so
      // Suggested Leads also surfaces leads caught by a "skip the inbox" filter
      // or auto-archived. SPAM/TRASH are already dropped server-side
      // (includeSpamTrash: false); -has:unsubscribe is added by the gateway.
      const labelExclusions = '-in:sent -in:drafts -in:chats';
      const term = this.searchTerm?.trim();
      const q = [term, dateFilter, labelExclusions].filter(Boolean).join(' ');
      const resp = await firstValueFrom(
        this._gmailService.listMessages$({
          maxResults: this.pageSize,
          pageToken,
          listingUuid: this.listingUuid,
          q
        })
      );
      if (this.isMasquerading) return;

      allEmails.push(...(resp?.data ?? []));

      if (!resp?.nextPageToken) break;
      pageToken = resp.nextPageToken;
    }

    this.inbox = allEmails.slice(0, this.maxTotalEmails);
    await this.loadEmailThreads();
    this._allLeads = [...this.inbox];
  }

  private async fetchLeadsOfType(index: number, emailThreadType: EmailThreadType): Promise<void> {
    try {
      const threadsResult = await firstValueFrom(
        this._emailThreadsService.getEmailThreads$({ type: emailThreadType, listingUuid: this.listingUuid, limit: this.pageSize, page: index + 1 })
      );
      const pageThreads = threadsResult?.data ?? [];

      this.approxTotal = Math.min(this.maxTotalEmails, threadsResult?.total ?? 0);
      this.pageIndex = index;

      this.emailThreadMap.clear();
      this._categorizedThreadIds.clear();
      pageThreads.forEach(t => {
        const key = this._threadKey(t.threadId, t.messageId);
        this.emailThreadMap.set(key, t);
        this._categorizedThreadIds.add(key);
      });

      if (!pageThreads.length) {
        this.inbox = [];
        this.setPagination();
        return;
      }

      // Thread-level records (messageId is null/undefined) → fetch the whole thread
      const threadLevelIds = [...new Set(pageThreads.filter(t => !t.messageId).map(t => t.threadId))];
      // Message-level records (aggregator) → fetch individual messages
      const messageLevelIds = [...new Set(pageThreads.filter(t => !!t.messageId).map(t => t.messageId!))];

      const [threadResp, messageResp] = await Promise.all([
        threadLevelIds.length ? firstValueFrom(this._gmailService.batchGetThreads$(threadLevelIds)) : Promise.resolve({ data: [] }),
        messageLevelIds.length ? firstValueFrom(this._gmailService.batchGetMessages$(messageLevelIds)) : Promise.resolve({ data: [] })
      ]);
      if (this.isMasquerading) return;

      // Re-order results to match the original DB page sequence.
      // Concatenating thread results before message results would lose the DB ordering
      // on mixed pages (e.g. [t1, agg-msg, t3] becoming [t1, t3, agg-msg]).
      const threadByThreadId = new Map((threadResp?.data ?? []).map(m => [m.threadId, m]));
      const messageByMessageId = new Map((messageResp?.data ?? []).map(m => [m.id, m]));
      let results = pageThreads
        .map(t => (t.messageId ? messageByMessageId.get(t.messageId) : threadByThreadId.get(t.threadId)))
        .filter(Boolean) as Email[];

      const term = this.searchTerm?.trim().toLowerCase();
      if (term) {
        results = results.filter(
          m =>
            m.subject?.toLowerCase().includes(term) ||
            m.from?.email?.toLowerCase().includes(term) ||
            m.from?.name?.toLowerCase().includes(term) ||
            m.snippet?.toLowerCase().includes(term)
        );
      }

      this.inbox = results;

      // Filter out emails that also have an associated enquiry record.
      // A thread can have both a not_lead DB record and an enquiry DB record if an
      // enquiry was later created from the same email. The enquiry takes precedence —
      // the email should not appear in the Not Leads list.
      const linkedThreadIds = [...new Set(this.inbox.map(m => m.threadId).filter(Boolean))].join(',');
      if (linkedThreadIds) {
        const allRecords = await firstValueFrom(
          this._emailThreadsService.getEmailThreads$({ threadId: linkedThreadIds, listingUuid: this.listingUuid, limit: 500 })
        );
        const enquiryKeys = new Set<string>();
        (allRecords?.data ?? []).forEach(t => {
          if (t.type === EmailThreadType.Enquiry) {
            enquiryKeys.add(t.messageId ? this._threadKey(t.threadId, t.messageId) : t.threadId);
          }
        });
        if (enquiryKeys.size) {
          this.inbox = this.inbox.filter(m => !enquiryKeys.has(this._threadKey(m.threadId, m.id)) && !enquiryKeys.has(m.threadId));
        }
      }

      this.setPagination();
    } catch (err) {
      console.error('Failed to load email threads by type', err);
    }
  }

  private async fetchAllEmails(index: number): Promise<void> {
    const pageToken = this.pageTokens[index] || undefined;
    const resp = await firstValueFrom(
      this._gmailService.listMessages$({
        maxResults: this.pageSize,
        pageToken,
        q: [this.buildQuery(), '-in:sent -in:drafts -in:chats'].filter(Boolean).join(' ')
      })
    );
    if (this.isMasquerading) return;

    if (typeof resp?.resultSizeEstimate === 'number' && this.approxTotal === null) {
      this.approxTotal = Math.min(this.maxTotalEmails, resp.resultSizeEstimate);
    }

    this.inbox = resp?.data ?? [];
    this.pageIndex = index;
    this.pageTokens[index + 1] = resp?.nextPageToken ?? null;
    this.setPagination();

    await this.loadEmailThreads();
  }

  private async loadEmailThreads(): Promise<void> {
    if (!this.inbox.length) return;

    const threadIds = [...new Set(this.inbox.map(m => m.threadId).filter(Boolean))].join(',');
    if (!threadIds) return;

    try {
      const result = await firstValueFrom(
        this._emailThreadsService.getEmailThreads$({ threadId: threadIds, listingUuid: this.listingUuid, limit: 500 }).pipe(takeUntil(this.destroy$)),
        { defaultValue: null }
      );
      if (!result) return;
      this.emailThreadMap.clear();
      this._categorizedThreadIds.clear();
      const hiddenThreadIds = new Set<string>();
      (result?.data ?? []).forEach(t => {
        const key = this._threadKey(t.threadId, t.messageId);
        // Prefer enquiry-type record so hasLinkedEnquiry / navigateToLinkedEnquiry work correctly
        const existing = this.emailThreadMap.get(key);
        if (!existing || t.type === EmailThreadType.Enquiry) {
          this.emailThreadMap.set(key, t);
        }
        if (t.type === EmailThreadType.Enquiry || t.type === EmailThreadType.NotLead || t.type === EmailThreadType.AutoNotALead) {
          this._categorizedThreadIds.add(key);
        }
        if (this.isLeadsMode && !this.isAllEmailsMode && (t.type === EmailThreadType.NotLead || t.type === EmailThreadType.AutoNotALead)) {
          // For thread-level records, hide the whole thread; for message-level, only hide that message
          hiddenThreadIds.add(t.messageId ? key : t.threadId);
        }
      });
      if (this.isLeadsMode) {
        // Hide by composite key (message-level) or threadId (thread-level)
        this.inbox = this.inbox.filter(m => !hiddenThreadIds.has(this._threadKey(m.threadId, m.id)) && !hiddenThreadIds.has(m.threadId));
      }
    } catch (err) {
      console.error('Failed to load email threads', err);
    }
  }

  /**
   * Returns the composite map key for a message.
   * Aggregator records are keyed on `threadId::messageId`; thread-level on `threadId` alone.
   */
  private _threadKey(threadId: string, messageId?: string | null): string {
    return messageId ? `${threadId}::${messageId}` : threadId;
  }

  /**
   * Looks up the EmailThread for a Gmail message.
   * Tries the composite key first (aggregator case), falls back to threadId (normal case).
   */
  private _lookupThread(message: Email): EmailThread | undefined {
    return this.emailThreadMap.get(this._threadKey(message.threadId, message.id)) ?? this.emailThreadMap.get(message.threadId);
  }

  hasLinkedEnquiry(message: Email): boolean {
    return this._lookupThread(message)?.type === EmailThreadType.Enquiry;
  }

  isAutoNotALead(message: Email): boolean {
    return this._lookupThread(message)?.type === EmailThreadType.AutoNotALead;
  }

  canImportAsLead(message: Email): boolean {
    // Check composite key first (aggregator), then thread-level key (non-aggregator)
    return !this._categorizedThreadIds.has(this._threadKey(message.threadId, message.id)) && !this._categorizedThreadIds.has(message.threadId);
  }

  get suggestedLeads(): Email[] {
    return this.inbox.filter(m => this.canImportAsLead(m));
  }

  importAllLeads(): void {
    const leads = this.suggestedLeads;
    if (!leads.length || !this.connectedEmail || this.importAllLeadsCooldown) return;

    this.importingAllLeads = true;
    this._startImportCooldown();
    this._enquiriesService
      .batchParseLeads$(
        leads.map(m => m.id),
        this.connectedEmail
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.importingAllLeads = false;
        })
      )
      .subscribe({
        next: () => {
          this._notificationsService.openSnackBar({ message: 'All leads queued for import', action: 'Close', type: NotificationTypes.SUCCESS });
        },
        error: () => {
          this._notificationsService.openSnackBar({ message: 'Failed to import leads', action: 'Close', type: NotificationTypes.ERROR });
        }
      });
  }

  getLinkedEnquiryUuid(message: Email): string | null {
    return this._lookupThread(message)?.enquiryUuid ?? null;
  }

  navigateToLinkedEnquiry(message: Email, event: MouseEvent): void {
    event.stopPropagation();
    const enquiryUuid = this.getLinkedEnquiryUuid(message);
    if (!enquiryUuid || !this.listingUuid) return;
    this._router.navigate(['/su', this.listingUuid, 'enquiries', 'enquiry', enquiryUuid]);
  }

  importEnquiry(message: Email, event: MouseEvent): void {
    event.stopPropagation();
    if (this.importingEmailId) return;

    this.importingEmailId = message?.id;

    this._gmailService
      .getMessage$(message?.id)
      .pipe(
        first(),
        switchMap(fullEmail => {
          console.log(fullEmail);
          if (!fullEmail?.textBody) {
            throw new Error('Email has no text body to parse.');
          }
          const email = fullEmail.from.email ?? '';
          const body = fullEmail.textBody;
          const html = fullEmail.htmlBody;
          const emailText = `Email Sent On: ${fullEmail.date} Email: ${fullEmail.from.email} Subject: ${fullEmail.subject} Body: ${body}`;

          return this._enquiriesService.getEnquiryFromDetails(emailText, email, body, html).pipe(
            first(),
            switchMap((enquiry: Enquiry) => {
              const customerEmail = enquiry.email ?? email;
              return this._customerService.getCustomers({ limit: 200 }).pipe(
                take(1),
                catchError(() => of({ data: [] })),
                map(customers => {
                  const customer = customers.data.find(c => c.email?.toLowerCase() === customerEmail.toLowerCase());
                  return { enquiry, customer };
                })
              );
            })
          );
        }),
        finalize(() => (this.importingEmailId = null))
      )
      .subscribe({
        next: ({ enquiry, customer }) => {
          this._notificationsService.openSnackBar({
            message: 'Successfully extracted enquiry details from email.',
            action: 'Close',
            type: NotificationTypes.SUCCESS
          });
          const threadDate = message.internalDate ? new Date(Number(message.internalDate)).toISOString() : (message.date ?? null);

          const route = customer ? ['/su', this.listingUuid, 'enquiries', 'add', customer.uuid] : ['/su', this.listingUuid, 'enquiries', 'add'];
          this._router.navigate(route, {
            queryParams: enquiry
              ? {
                  generatedEnquiry: true,
                  threadId: message.threadId ?? null,
                  threadDate,
                  messageId: message.id ?? null,
                  senderEmail: message.from?.email ?? null,
                  recordGenerator: 'manual_ai_from_email_parser'
                }
              : {},
            replaceUrl: true
          });
        },
        error: error => {
          this._notificationsService.openSnackBar({
            message: error.message || 'Error extracting enquiry details from email. Please try again.',
            action: 'Close',
            type: NotificationTypes.ERROR
          });
        }
      });
  }

  private async ensureTokenFor(targetIndex: number): Promise<void> {
    if (this.isMasquerading) return;

    if (this.pageTokens[targetIndex] !== undefined) return;

    let i = this.pageTokens.findIndex(t => t === undefined);
    if (i === -1) i = this.pageTokens.length;
    i = Math.max(i - 1, 0);

    while (i < targetIndex) {
      const token = this.pageTokens[i] || undefined;
      const resp = await firstValueFrom(
        this._gmailService.listMessages$(
          this.isAllMailMode
            ? { maxResults: this.pageSize, pageToken: token, q: [this.buildQuery(), '-in:sent -in:drafts -in:chats'].filter(Boolean).join(' ') }
            : { maxResults: this.pageSize, pageToken: token, labelIds: this.labelIds, q: this.buildQuery() }
        )
      );
      this.pageTokens[i + 1] = resp?.nextPageToken ?? null;
      if (!resp?.nextPageToken) break;
      i++;
    }
  }

  onValueChanges(event: { detail: ModelValues<string> }) {
    const value = event.detail['search'];
    this.search$.next(String(value?.value).trim());
  }

  async onPageChange(evt: CustomEvent<{ pageIndex: number; prevPage: number }>) {
    const requested1Based = evt.detail.pageIndex;
    const targetIndex = Math.max(0, requested1Based - 1);
    if (targetIndex === this.pageIndex) return;

    if (this.isMasquerading) {
      await this._router.navigate([], {
        relativeTo: this._route,
        queryParams: { page: 1, pageToken: null },
        queryParamsHandling: 'merge',
        replaceUrl: false
      });
      return;
    }

    // Cursor-based navigation is not used in suggested leads mode (client-side pagination)
    if (!this.isSuggestedLeadsMode) {
      await this.ensureTokenFor(targetIndex);
    }
    const tokenForTarget = this.isSuggestedLeadsMode ? null : (this.pageTokens[targetIndex] ?? null);

    await this._router.navigate([], {
      relativeTo: this._route,
      queryParams: {
        page: requested1Based,
        pageToken: tokenForTarget || null,
        q: this.searchTerm || null
      },
      queryParamsHandling: 'merge',
      replaceUrl: false
    });

    await this.fetchPage(targetIndex);
  }

  async refresh(): Promise<void> {
    if (this.loading) return;

    // when masquerading, just reset UI and stop
    if (this.isMasquerading) {
      this.refreshing = true;
      this.pageTokens = [null];
      this.pageIndex = 0;
      this.approxTotal = 0;
      this.inbox = [];
      this.pagination = { page: 1, pageCount: 1 };

      await this._router.navigate([], {
        relativeTo: this._route,
        queryParams: { page: 1, pageToken: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });

      this.refreshing = false;
      return;
    }

    this.refreshing = true;

    this.pageTokens = [null];
    this.pageIndex = 0;
    this.approxTotal = null;
    this.inbox = [];
    this.pagination = null;
    this._allLeads = [];

    await this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { page: 1, pageToken: null, q: this.searchTerm || null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });

    await this.fetchPage(0);
    this.refreshing = false;
  }

  navigateToEmail(id: string, event?: MouseEvent) {
    if (!id) return;
    if (event && (event.metaKey || event.ctrlKey)) {
      const tree = this._router.createUrlTree([id], { relativeTo: this._route, queryParamsHandling: 'preserve' });
      window.open(this._router.serializeUrl(tree), '_blank');
      return;
    }
    this._router.navigate([id], { relativeTo: this._route, queryParamsHandling: 'preserve' });
  }

  getDisplaySnippet(email: Email): string {
    const el = document.createElement('textarea');
    el.innerHTML = email.snippet || '';
    return el.value;
  }

  getDisplayDate(email: Email): string {
    const d = email.internalDate ? new Date(Number(email.internalDate)) : email.date ? new Date(email.date) : null;
    if (!d || isNaN(+d)) return '';
    return isToday(d) ? format(d, 'HH.mm') : format(d, 'd MMM', { locale: enGB });
  }

  getIsUnread(email: Email): boolean {
    return Array.isArray(email.labelIds) && email.labelIds.includes('UNREAD');
  }

  getIsSent(message: Email): boolean {
    // Pick the one that matches your data model (Gmail labelIds is common)
    return message.labelIds?.includes('SENT');
  }

  getDisplayPerson(message: Email): string {
    const isSent = this.getIsSent(message);

    const to = message.to;
    const toList = Array.isArray(to) ? to : to ? [to] : [];
    const primaryTo = toList[0];

    const party = isSent ? primaryTo : message.from;

    const base = party?.name || party?.email || '';
    if (!isSent) return base;

    // Optional: if multiple recipients, show “Alice +2”
    const extra = toList.length > 1 ? ` +${toList.length - 1}` : '';
    return base ? `${base}${extra}` : '';
  }

  dismissInfoBanner(): void {
    this.infoBannerDismissed = true;
    localStorage.setItem(EmailInboxComponent.INFO_BANNER_DISMISSED_KEY, 'true');
  }

  get isBannerClickDisabled(): boolean {
    return Date.now() - this.bannerClickedAt < EmailInboxComponent.BANNER_COOLDOWN_MS;
  }

  onBannerClick(): void {
    if (this.isBannerClickDisabled) return;
    this.bannerClickedAt = Date.now();
    this._router.navigate(['/su', this.listingUuid, 'integrations', 'email']);
  }

  openNotLeadDialog(message: Email, event: MouseEvent): void {
    event.stopPropagation();
    this.notLeadDialogMessage = message;
  }

  onNotLeadAction(action: NotLeadAction): void {
    const message = this.notLeadDialogMessage;
    if (!message) return;
    if (action === 'thread') this._excludeThread(message);
    else if (action === 'address') this._excludeAddress(message);
    else if (action === 'domain') this._excludeDomain(message);
  }

  private async _excludeThread(message: Email): Promise<void> {
    try {
      await firstValueFrom(
        this._emailThreadsService.createEmailThread$({
          type: EmailThreadType.NotLead,
          threadId: message.threadId,
          messageId: message.id ?? undefined,
          senderEmail: message.from?.email ?? undefined,
          listingUuid: this.listingUuid,
          emailProvider: 'Gmail',
          updated: new Date().toISOString()
        })
      );
      if (this.isLeadsMode) {
        // For aggregator threads each message is independent — remove only the specific
        // message by composite key. For normal threads, remove all messages in the thread.
        const key = this._threadKey(message.threadId, message.id);
        const predicate = (m: Email) => this._threadKey(m.threadId, m.id) !== key;
        this.inbox = this.inbox.filter(predicate);
        this._allLeads = this._allLeads.filter(predicate);
        this.approxTotal = this._allLeads.length;
        this.setPagination();
      }
      this._notificationsService.openSnackBar({
        message: 'Email thread excluded from Leads inbox.',
        action: 'Close',
        type: NotificationTypes.SUCCESS
      });
    } catch {
      this._notificationsService.openSnackBar({
        message: 'Failed to exclude email thread. Please try again.',
        action: 'Close',
        type: NotificationTypes.ERROR
      });
    }
  }

  private async _excludeAddress(message: Email): Promise<void> {
    const address = message.from?.email;
    if (!address || !this.listing) return;
    try {
      await firstValueFrom(this._emailSettingsService.addLeadExclusionEntry$(address, this.listing));
      this._store.dispatch(new fromAppActions.GetListing());
      if (this.isLeadsMode) {
        const predicate = (m: Email) => m.from?.email !== address;
        this.inbox = this.inbox.filter(predicate);
        this._allLeads = this._allLeads.filter(predicate);
        this.approxTotal = this._allLeads.length;
        this.setPagination();
      }
      this._notificationsService.openSnackBar({
        message: `Emails from ${address} excluded from Leads inbox.`,
        action: 'Close',
        type: NotificationTypes.SUCCESS
      });
    } catch {
      this._notificationsService.openSnackBar({
        message: 'Failed to exclude email address. Please try again.',
        action: 'Close',
        type: NotificationTypes.ERROR
      });
    }
  }

  private async _excludeDomain(message: Email): Promise<void> {
    const domain = message.from?.email?.split('@')[1];
    if (!domain || !this.listing) return;
    try {
      await firstValueFrom(this._emailSettingsService.addLeadExclusionEntry$(domain, this.listing));
      this._store.dispatch(new fromAppActions.GetListing());
      if (this.isLeadsMode) {
        const predicate = (m: Email) => m.from?.email?.split('@')[1] !== domain;
        this.inbox = this.inbox.filter(predicate);
        this._allLeads = this._allLeads.filter(predicate);
        this.approxTotal = this._allLeads.length;
        this.setPagination();
      }
      this._notificationsService.openSnackBar({
        message: `Emails from ${domain} excluded from Leads inbox.`,
        action: 'Close',
        type: NotificationTypes.SUCCESS
      });
    } catch {
      this._notificationsService.openSnackBar({
        message: 'Failed to exclude domain. Please try again.',
        action: 'Close',
        type: NotificationTypes.ERROR
      });
    }
  }

  private _checkImportCooldown(): void {
    const stored = sessionStorage.getItem(this._importCooldownKey);
    if (!stored) return;

    const remaining = Number(stored) - Date.now();
    if (remaining > 0) {
      this.importAllLeadsCooldown = true;
      this._cooldownTimer = setTimeout(() => {
        this.importAllLeadsCooldown = false;
        sessionStorage.removeItem(this._importCooldownKey);
      }, remaining);
    } else {
      sessionStorage.removeItem(this._importCooldownKey);
    }
  }

  private _startImportCooldown(): void {
    const cooldownUntil = Date.now() + this._importCooldownMs;
    sessionStorage.setItem(this._importCooldownKey, String(cooldownUntil));
    this.importAllLeadsCooldown = true;

    if (this._cooldownTimer) clearTimeout(this._cooldownTimer);
    this._cooldownTimer = setTimeout(() => {
      this.importAllLeadsCooldown = false;
      sessionStorage.removeItem(this._importCooldownKey);
    }, this._importCooldownMs);
  }

  ngOnDestroy(): void {
    if (this._cooldownTimer) clearTimeout(this._cooldownTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
